import React from 'react'
import { CampaignChannel, ChannelVariant } from '../../../types/campaigns'
import { WhatsAppPreview } from './tiptap/WhatsAppPreview'
import { EmailPreview } from './EmailPreview'
import { DEFAULT_EMAIL_CONFIG } from './EmailDesignSettings'

interface VariantPreviewProps {
  variant: ChannelVariant
  variables?: Record<string, string>
  liveBodyText?: string
}

const CHANNEL_ICONS: Record<CampaignChannel, string> = {
  SMS: '💬',
  EMAIL: '📧',
  WHATSAPP: '📱',
  TELEGRAM: '✈️',
}

const CHANNEL_COLORS: Record<CampaignChannel, string> = {
  SMS: '#6c757d',
  EMAIL: '#0d6efd',
  WHATSAPP: '#25D366',
  TELEGRAM: '#0088cc',
}

export const VariantPreview: React.FC<VariantPreviewProps> = ({ variant, variables = {}, liveBodyText }) => {
  // Replace variables in text
  const replaceVariables = (text: string) => {
    let result = text || ''
    Object.entries(variables).forEach(([key, value]) => {
      result = result.replace(new RegExp(`{{${key}}}`, 'g'), value)
    })
    return result
  }

  const stripHtml = (html?: string) => {
    if (!html) return ''
    return html.replace(/<[^>]*>/g, '').trim()
  }

  // Use live text if provided, otherwise use saved variant text
  const bodyTextToShow =
    liveBodyText !== undefined && liveBodyText !== null && liveBodyText !== ''
      ? liveBodyText
      : variant.bodyText || stripHtml(variant.bodyHtml)

  const renderContent = () => {
    const channelColor = CHANNEL_COLORS[variant.channel]

    switch (variant.channel) {
      case 'SMS':
        return (
          <div className='border rounded p-3' style={{ backgroundColor: '#f8f9fa' }}>
            <div className='d-flex align-items-start'>
              <div
                className='badge me-2'
                style={{ backgroundColor: channelColor, fontSize: '16px' }}
              >
                {CHANNEL_ICONS.SMS}
              </div>
              <div className='flex-grow-1'>
                <div
                  className='alert alert-secondary mb-0'
                  style={{
                    backgroundColor: '#fff',
                    color: '#000',
                    maxWidth: '300px',
                    fontSize: '14px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {replaceVariables(bodyTextToShow)}
                </div>
                <small className='text-muted'>
                  {bodyTextToShow.length} caracteres
                  {variant.smsMaxLength && ` / ${variant.smsMaxLength} max`}
                </small>
              </div>
            </div>
          </div>
        )

      case 'EMAIL':
        // Parse email config or use default
        let emailConfig = DEFAULT_EMAIL_CONFIG
        if (variant.metadata) {
          try {
            let meta = variant.metadata
            if (typeof meta === 'string') meta = JSON.parse(meta)
            if (meta.email_config) {
              emailConfig = {
                ...DEFAULT_EMAIL_CONFIG,
                ...meta.email_config,
                header: { ...DEFAULT_EMAIL_CONFIG.header, ...meta.email_config.header },
                footer: { ...DEFAULT_EMAIL_CONFIG.footer, ...meta.email_config.footer },
                styles: { ...DEFAULT_EMAIL_CONFIG.styles, ...meta.email_config.styles }
              }
            }
          } catch (e) { }
        }

        // We need to inject variables into the html before previewing
        const htmlToPreview = variant.bodyHtml ? replaceVariables(variant.bodyHtml) : `<pre>${replaceVariables(bodyTextToShow)}</pre>`

        return (
          <div className='d-flex flex-column gap-3'>
            <div className='border rounded p-3 bg-white'>
              <strong>Asunto:</strong> {replaceVariables(variant.subject)}
            </div>

            <EmailPreview config={emailConfig} bodyHtml={htmlToPreview} />
          </div>
        )

      case 'WHATSAPP':
        if (!bodyTextToShow?.trim()) {
          return (
            <div className='alert alert-warning'>
              Agrega texto en la variante para ver la vista previa de WhatsApp.
            </div>
          )
        }

        return (
          <div>
            <WhatsAppPreview message={replaceVariables(bodyTextToShow)} />
            {variant.whatsappTemplateId && (
              <div className='alert alert-info mt-3 small'>
                <strong>Template ID:</strong> {variant.whatsappTemplateId} ({variant.whatsappLanguage})
              </div>
            )}
          </div>
        )

      case 'TELEGRAM':
        return (
          <div className='border rounded p-3' style={{ backgroundColor: '#f0f2f5' }}>
            <div className='d-flex align-items-start'>
              <div
                className='badge me-2'
                style={{ backgroundColor: channelColor, fontSize: '16px' }}
              >
                {CHANNEL_ICONS.TELEGRAM}
              </div>
              <div className='flex-grow-1'>
                <div
                  className='bg-white rounded p-3 shadow-sm'
                  style={{
                    maxWidth: '350px',
                    fontSize: '14px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {replaceVariables(bodyTextToShow)}
                </div>
              </div>
            </div>
          </div>
        )
    }
  }

  return (
    <div className='card preview-template'>
      <div className='card-body'>{renderContent()}</div>
    </div>
  )
}
